// @flow strict

import React, {
  type Node,
  createContext,
  useContext,
  useCallback,
} from 'react';

import { localizations } from '../../content/localization/localization';

/**
 * Type of component properties.
 */
type LanguageWrapperProps = {
  language: string,
  children: Node,
};

/**
 * Type of context value.
 */
type LanguageContextType = {
  language: string,
  loc: (key: string) => string,
  languages: Array<string>,
} | null;

// available languages
const languages = ['en', 'de'];

// create context
const LanguageContext = createContext<LanguageContextType>(null);

/**
 * Lookup translation for passed in key-language pair.
 *
 * @param {string} key - key of translatable text
 * @param {string} language - language to lookup.
 *
 * @returns {string} - translated string or key
 */
const loc = (key: string, language: string): string => {
  const strings = localizations[language];

  // if translations for language do not exist
  // return key
  if (strings == null) {
    return key;
  }

  // if translation for key does not exist
  // return key
  const string = strings[key];
  if (string == null) {
    return key;
  }

  // return translated string
  return string;
};

/**
 * Hook to provide access to context in other components.
 *
 * @returns {LanguageContext} value of context
 */
export const useLanguageContext = () => {
  const languageContext = useContext(LanguageContext);

  // if value is empty, throw error
  // which means context has not been set up properly.
  if (languageContext == null) {
    throw new Error(
      'Language context is missing, did you forget to wrap your component inside the LanguageWrapper component?',
    );
  }

  return languageContext;
};

/**
 * Wrapper component to set up context.
 *
 * @param {LanguageWrapperProps} props - component propertis
 *
 * @returns {Node}
 */
export const LanguageWrapper = ({
  language,
  children,
}: LanguageWrapperProps) => {
  // return context provider with current values.
  return (
    <LanguageContext.Provider
      value={{
        language,
        languages,
        loc: useCallback(
          (key: string) => {
            return loc(key, language);
          },
          [language],
        ),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
